import React from 'react';
import { cn } from '../../utils/cn';
import type { GridProps } from '../../types/components';

const gapClasses = {
  sm: 'gap-2',
  md: 'gap-4',
  lg: 'gap-6',
  xl: 'gap-8',
};

export const Grid = React.forwardRef<HTMLDivElement, GridProps>(
  (
    {
      columns = 1,
      gap = 'md',
      rows,
      autoFit = false,
      autoFill = false,
      children,
      className,
      testId,
      ...props
    },
    ref
  ) => {
    const baseClasses = ['grid'];
    
    const gapClass = gapClasses[gap] || gapClasses.md;

    // Handle responsive columns
    const getColumnClasses = () => {
      if (typeof columns === 'number') {
        return `grid-cols-${columns}`;
      }
      
      // Handle responsive object
      const responsiveClasses: string[] = [];
      Object.entries(columns).forEach(([breakpoint, cols]) => {
        if (breakpoint === 'default') {
          responsiveClasses.push(`grid-cols-${cols}`);
        } else {
          responsiveClasses.push(`${breakpoint}:grid-cols-${cols}`);
        }
      });
      return responsiveClasses.join(' ');
    };

    const getGridTemplateColumns = () => {
      if (autoFit) {
        return { gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))' };
      }
      if (autoFill) {
        return { gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))' };
      }
      return {};
    };

    const classes = cn(
      baseClasses,
      getColumnClasses(),
      gapClass,
      {
        [`grid-rows-${rows}`]: rows,
      },
      className
    );

    const style = getGridTemplateColumns();

    return (
      <div
        ref={ref}
        className={classes}
        style={style}
        data-testid={testId}
        {...props}
      >
        {children}
      </div>
    );
  }
);

Grid.displayName = 'Grid';